import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Search, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getMediaUrl } from '../utils/mediaUrl';
import { useCT } from '../utils/creationTranslations';

const CURRENCY_LABEL = {
  EUR: '€', USD: '$', GBP: '£', CAD: 'CA$', CHF: 'CHF', AUD: 'A$',
  JPY: '¥', CNY: '¥', INR: '₹', RUB: '₽', BRL: 'R$', AED: 'AED',
  MAD: 'DH', XOF: 'FCFA', KES: 'KSh',
};

/**
 * SponsorPicker — modal that lets a creator pick a verified PRO sponsor,
 * choose an amount + currency, and submit a sponsorship request before
 * publishing a Gim Toli / Podcast / Short Film.
 *
 * Calls POST /api/monetization/request once "Envoyer la demande" is clicked.
 *
 * Props:
 *  - open, onClose
 *  - contentId, contentKind ('gim_toli' | 'podcast' | 'short_film')
 *  - onSubmitted({ requestId }) — fires after a successful request creation
 *  - onShareDirect()  — fires when user opts to publish without sponsor
 */
export default function SponsorPicker({ open, onClose, contentId, contentKind, onSubmitted, onShareDirect }) {
  const ct = useCT();
  const [eligibility, setEligibility] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [offers, setOffers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null); // { kind: 'sponsor'|'offer', data }
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const [el, of] = await Promise.all([
          axios.get('/api/monetization/eligibility'),
          axios.get('/api/monetization/offers', { params: { kind: contentKind } }),
        ]);
        if (!cancel) {
          setEligibility(el.data);
          setOffers(of.data?.offers || []);
        }
      } catch (_e) { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [open, contentKind]);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length === 0) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get('/api/monetization/sponsors/search', { params: { q } });
        if (!cancelled) setResults(r.data?.sponsors || []);
      } catch (_e) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  const minDuration = useMemo(() => eligibility?.min_duration_sec?.[contentKind] || 0, [eligibility, contentKind]);
  const isEligible = !!eligibility?.is_eligible;
  const followers = eligibility?.followers ?? 0;

  if (!open) return null;

  const submit = async () => {
    if (!picked) return toast.error(ct('spo.pickFirst'));
    setSubmitting(true);
    try {
      if (picked.kind === 'offer') {
        // Apply to a public offer (amount + currency are dictated by the offer)
        await axios.post(`/api/monetization/offers/${picked.data.id}/apply`, {
          content_id: contentId,
          content_kind: contentKind,
        });
      } else {
        const amt = parseFloat(amount);
        if (!Number.isFinite(amt) || amt <= 0) { setSubmitting(false); return toast.error(ct('spo.invalidAmount')); }
        await axios.post('/api/monetization/request', {
          content_id: contentId,
          content_kind: contentKind,
          sponsor_id: picked.data.id,
          amount: amt,
          currency,
        });
      }
      toast.success(ct('spo.okSubmitted'));
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || ct('spo.errRequest');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="sponsor-picker-modal">
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-gray-900 text-white rounded-t-3xl sm:rounded-3xl border border-white/10">
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur-md border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">{ct('spo.title')}</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="sponsor-picker-close" aria-label={ct('spo.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Eligibility banner */}
        {!isEligible && (
          <div className="m-4 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm">
            {ct('spo.lockedBanner', { n: followers.toLocaleString('fr-FR') })}
            <button
              type="button"
              onClick={() => { onShareDirect?.(); onClose?.(); }}
              className="block mt-3 px-4 py-2 rounded-full bg-amber-400 text-black font-semibold"
              data-testid="sponsor-picker-skip"
            >
              {ct('spo.shareWithout')}
            </button>
          </div>
        )}

        {isEligible && (
          <>
            {/* Search input */}
            <div className="p-4 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={ct('spo.searchPlaceholder')}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="w-full pl-9 pr-3 py-3 bg-white/5 border border-white/10 rounded-xl text-sm focus:outline-none focus:border-magenta-500"
                  data-testid="sponsor-search-input"
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">{ct('spo.onlyProNote')}</p>
            </div>

            {/* Search results */}
            <div className="px-4 space-y-2 min-h-[60px]">
              {searching && <div className="flex items-center gap-2 text-gray-400 text-sm py-2"><Loader2 className="w-4 h-4 animate-spin" /> {ct('spo.searching')}</div>}
              {!searching && q.trim() && results.length === 0 && (
                <p className="text-sm text-gray-500 py-2">{ct('spo.noResults')}</p>
              )}
              {results.map((s) => {
                const isPicked = picked?.kind === 'sponsor' && picked?.data?.id === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setPicked({ kind: 'sponsor', data: s })}
                    className={`w-full p-3 rounded-xl border transition-all flex items-center gap-3 ${isPicked ? 'bg-magenta-500/15 border-magenta-500/50' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                    data-testid={`sponsor-result-${s.id}`}
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                      {s.profile_image_url ? <img src={getMediaUrl(s.profile_image_url)} alt={s.name} className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="font-semibold flex items-center gap-1 truncate">{s.company_name || s.name} <CheckCircle className="w-3.5 h-3.5 text-blue-400" /></div>
                      {s.username && <div className="text-xs text-gray-400 truncate">@{s.username}</div>}
                      {s.website_url && <div className="text-xs text-magenta-300 truncate flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {s.website_url}</div>}
                    </div>
                    {isPicked && <CheckCircle className="w-5 h-5 text-magenta-400" />}
                  </button>
                );
              })}
            </div>

            {/* Active offers */}
            {offers.length > 0 && (
              <div className="px-4 pt-4 mt-2 border-t border-white/10 space-y-2">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-emerald-300">{ct('spo.activeOffers')}</h3>
                <p className="text-xs text-gray-500 mb-1">{ct('spo.offersHint')}</p>
                {offers.map((of) => {
                  const isPickedOffer = picked?.kind === 'offer' && picked?.data?.id === of.id;
                  return (
                    <button
                      key={of.id}
                      type="button"
                      onClick={() => setPicked({ kind: 'offer', data: of })}
                      className={`w-full p-3 rounded-xl border text-left transition-all ${isPickedOffer ? 'bg-emerald-500/15 border-emerald-500/50' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                      data-testid={`offer-result-${of.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold truncate">{of.title}</div>
                        <div className="text-emerald-300 font-bold whitespace-nowrap">{of.amount} {of.currency}</div>
                      </div>
                      <div className="text-xs text-gray-400 line-clamp-2">{of.description}</div>
                      <div className="mt-1 text-[11px] text-gray-500">{ct('spo.byShort')} <span className="text-cyan-300">{of.sponsor_name}</span> · {ct('spo.talkAtLeast', { n: of.min_talk_seconds })}</div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Amount + currency (only for direct sponsor selection — offers are fixed) */}
            {picked?.kind === 'sponsor' && (
              <div className="p-4 mt-2 border-t border-white/10 space-y-3">
                <h3 className="font-semibold">{ct('spo.proposal')}</h3>
                <div className="flex gap-2">
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder={ct('spo.amount')}
                    value={amount}
                    min={1}
                    step="0.01"
                    onChange={(e) => setAmount(e.target.value)}
                    className="flex-1 px-3 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500"
                    data-testid="sponsor-amount-input"
                  />
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="px-3 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500"
                    data-testid="sponsor-currency-select"
                  >
                    {(eligibility?.currencies || ['EUR', 'USD']).map((c) => (
                      <option key={c} value={c} className="bg-gray-900">
                        {c} {CURRENCY_LABEL[c] ? `(${CURRENCY_LABEL[c]})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500">
                  {minDuration > 0
                    ? ct('spo.minDur', { n: minDuration })
                    : ct('spo.willReview')}
                </p>
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  className="w-full py-3 rounded-full bg-gradient-to-r from-magenta-500 to-violet-600 font-bold disabled:opacity-50"
                  data-testid="sponsor-submit-btn"
                >
                  {submitting ? ct('spo.sending') : ct('spo.send')}
                </button>
                <button
                  type="button"
                  onClick={() => { onShareDirect?.(); onClose?.(); }}
                  className="w-full py-2 text-sm text-gray-400 hover:text-white"
                  data-testid="sponsor-skip-btn"
                >
                  {ct('spo.shareWithout')}
                </button>
              </div>
            )}

            {/* Offer apply submit */}
            {picked?.kind === 'offer' && (
              <div className="p-4 mt-2 border-t border-white/10 space-y-3">
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-sm">
                  <div className="font-semibold mb-1">{ct('spo.applyTitle')}</div>
                  <div className="text-gray-300">{picked.data.title} — <strong>{picked.data.amount} {picked.data.currency}</strong></div>
                  <div className="text-xs text-gray-500 mt-1">{ct('spo.talkAtLeast', { n: picked.data.min_talk_seconds })}</div>
                </div>
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  className="w-full py-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 font-bold disabled:opacity-50"
                  data-testid="offer-apply-btn"
                >
                  {submitting ? ct('spo.sending') : ct('spo.applyOffer')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
